import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  CheckCircle,
  Activity,
  Zap,
  Award,
  Search,
  ShieldCheck,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import ParticlesBackground from '../components/ParticlesBackground';
import PageHeader from '../components/PageHeader';

const ServiceCard = ({ icon: Icon, title, description, colorClass }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col items-start h-full">
    <div className={`p-3 rounded-lg mb-4 ${colorClass.bg} ${colorClass.text}`}>
      <Icon size={24} />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const CycleStep = ({ number, title, description }) => (
  <div className="relative pl-8 md:pl-0 md:text-center flex flex-col md:items-center group">
    <div className="absolute left-0 top-0 md:relative md:mx-auto w-8 h-8 md:w-12 md:h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold shadow-md mb-4 z-10">
      {number}
    </div>
    <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-gray-200 -z-0 transform translate-x-1/2 last:hidden"></div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm max-w-xs mx-auto">{description}</p>
  </div>
);

const PricingTier = ({ title, frequency, profile, colorClass, recommended = false, ctaLabel }) => (
  <div className={`relative bg-white border ${recommended ? 'border-blue-500 shadow-xl scale-105 z-10' : 'border-gray-200'} rounded-xl p-6 flex flex-col h-full`}>
    {recommended && (
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
        {recommended}
      </div>
    )}
    <div className={`text-center mb-6`}>
      <h3 className={`text-2xl font-bold ${colorClass}`}>{title}</h3>
      <div className="mt-2 inline-block bg-gray-100 rounded-full px-3 py-1 text-sm font-medium text-gray-600">
        {frequency}
      </div>
    </div>
    <p className="text-gray-600 text-sm text-center mb-6 flex-grow">{profile}</p>
    <button className={`w-full py-2 rounded-lg font-semibold transition-colors ${recommended ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
      {ctaLabel}
    </button>
  </div>
);

const SmcPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const heroImageUrl = '/images/smc/smc.png';

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const serviceItems = useMemo(() => {
    const items = t('smc.service_section.items', { returnObjects: true });
    const icons = [Search, AlertCircle, Zap];
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-600' },
      { bg: 'bg-amber-100', text: 'text-amber-600' },
      { bg: 'bg-green-100', text: 'text-green-600' }
    ];
    return items.map((item, i) => ({ ...item, icon: icons[i], colorClass: colors[i] }));
  }, [t]);

  const cycleSteps = useMemo(() => t('smc.cycle_section.steps', { returnObjects: true }), [t]);
  const modalities = useMemo(() => t('smc.modalities.items', { returnObjects: true }), [t]);
  const approvalItems = useMemo(() => {
    const items = t('smc.approval_section.items', { returnObjects: true });
    const icons = [CheckCircle, ShieldCheck, TrendingUp];
    const colors = ['bg-green-100 text-green-600', 'bg-blue-100 text-blue-600', 'bg-purple-100 text-purple-600'];
    return items.map((item, i) => ({ ...item, icon: icons[i], colorClass: colors[i] }));
  }, [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />
      
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('smc.popup.close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('smc.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('smc.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} />

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-screen">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
                <button
                  type="button"
                  onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {t('smc.back_btn')}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt={t('smc.hero_alt')}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Portada'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 p-8 md:p-14 min-h-screen flex flex-col justify-end max-w-7xl mx-auto">
              <div className="inline-flex items-center gap-3 bg-white/10 text-white px-4 py-2 rounded-full w-fit mb-6 backdrop-blur border border-white/15">
                <Activity size={18} />
                <span className="text-sm font-semibold">{t('smc.badge')}</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight max-w-5xl">
                {t('smc.title')}
              </h1>

              <p className="mt-6 text-xl md:text-2xl text-white/90 max-w-4xl font-medium">
                {t('smc.subtitle')}
              </p>

              <p className="mt-5 text-base md:text-lg text-white/75 max-w-4xl">
                {t('smc.description')}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('common.free_diagnostic')}
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">

          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('smc.service_section.title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('smc.service_section.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {serviceItems.map((item) => (
                <ServiceCard 
                  key={item.title}
                  icon={item.icon}
                  title={item.title}
                  description={item.desc}
                  colorClass={item.colorClass}
                />
              ))}
            </div>
          </section>

          <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-16">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-16">{t('smc.cycle_section.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
               <div className="hidden md:block absolute top-6 left-[16%] right-[16%] h-0.5 bg-gray-200 z-0"></div>
               {cycleSteps.map((step, i) => (
                 <CycleStep 
                   key={step.title}
                   number={i + 1}
                   title={step.title}
                   description={step.desc}
                 />
               ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('smc.modalities.title')}</h2>
            <p className="text-center text-gray-600 mb-12">{t('smc.modalities.subtitle')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-center">
              {modalities.map((m, i) => (
                <PricingTier 
                  key={m.title}
                  title={m.title}
                  frequency={m.frequency}
                  profile={m.profile}
                  colorClass={i === 1 ? 'text-blue-600' : i === 2 ? 'text-purple-600' : 'text-gray-700'}
                  recommended={m.recommended}
                  ctaLabel={t('smc.modalities.cta')}
                />
              ))}
            </div>
          </section>

          <section className="mb-24">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="w-full md:w-1/2">
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 -mt-4 -mr-4 bg-white/10 w-32 h-32 rounded-full blur-2xl"></div>
                  <Award size={48} className="mb-6 text-yellow-400" />
                  <h3 className="text-2xl font-bold mb-4">{t('smc.approval_section.title')}</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    {t('smc.approval_section.desc')}
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/2 space-y-6">
                {approvalItems.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${item.colorClass}`}>
                      <item.icon size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="text-center bg-gray-50 rounded-3xl p-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-[#141414] mb-6">{t('smc.final_cta.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              {t('smc.final_cta.desc')}
            </p>
            <h3 className="text-xl font-bold text-gray-900 mb-8">{t('smc.final_cta.question')}</h3>
            <button
              onClick={openPopup}
              className="bg-[#141414] hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-full transition transform hover:scale-105 shadow-xl"
            >
              {t('smc.final_cta.btn')}
            </button>
            <p className="text-sm text-gray-500 mt-6">
              {t('smc.final_cta.note')}
            </p>
          </section>

          </div>
        </div>
      </main>
    </div>
  );
};

export default SmcPage;

